/**
 * useBrokerages — data hook for the Brokerage Hub overview screen.
 *
 * Owns the dashboard summary + the filtered brokerage list, and the filter
 * state that drives the list. The search field is debounced so typing
 * doesn't fire a request per keystroke.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchBrokerages, fetchDashboardSummary } from '../api/brokerages';

const DEFAULT_FILTERS = { search: '', tier: 'all', status: 'all', sort: 'converted' };

export default function useBrokerages() {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [brokerages, setBrokerages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Tracks the latest request so a slow earlier response can't overwrite a
  // faster later one (classic filter race).
  const reqIdRef = useRef(0);

  const load = useCallback(
    async (activeFilters) => {
      if (!token) return;
      const reqId = ++reqIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const [summaryData, listData] = await Promise.all([
          fetchDashboardSummary(token),
          fetchBrokerages(token, activeFilters),
        ]);
        if (reqId !== reqIdRef.current) return; // a newer request won
        setSummary(summaryData);
        setBrokerages(listData?.brokerages || []);
      } catch (e) {
        if (reqId !== reqIdRef.current) return;
        setError(
          e?.response?.data?.detail ||
            e?.response?.data?.error ||
            e?.message ||
            'Could not load the Brokerage Hub',
        );
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    },
    [token],
  );

  // Debounced reload whenever filters or token change.
  useEffect(() => {
    const t = setTimeout(() => load(filters), 250);
    return () => clearTimeout(t);
  }, [filters, load]);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  return {
    summary,
    brokerages,
    loading,
    error,
    filters,
    updateFilter,
    resetFilters,
    refresh: () => load(filters),
  };
}
